import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, BookIcon, AlertCircleIcon, TabletIcon, HeadphonesIcon, BookOpenIcon, BookTextIcon, LayersIcon } from 'lucide-react';
import { useBrand } from '../../hooks/useBrand';
import { useAfterTideUser } from '../../context/AfterTideUserContext';
import { getLinkedStoryForBook } from '../../data/linkedEditions';
import { flattenWarnings } from '../../utils/filterVocabulary';
// Define types for our component props
interface Book {
  id: string;
  title: string;
  author: string;
  coverImage: string;
  rating?: number;
  reviewCount?: number;
  genres?: string[];
  genre?: string; // Support single genre from MyBookshelf
  subgenres?: string[];
  subgenre?: string; // Support single subgenre from MyBookshelf
  spiceLevel?: number;
  burnLevel?: number;
  darknessLevel?: number;
  endingType?: string;
  relationshipType?: string;
  contentWarnings?: string[];
  seriesName?: string;
  series?: string; // Support series from MyBookshelf
  seriesStatus?: string;
  seriesNumber?: number;
  totalSeriesBooks?: number;
  formats?: string[]; // Array of available formats
  isNew?: boolean;
  isDebut?: boolean;
  coAuthor?: string; // Added for collaborations
  consentFramework?: string; // AfterTide specific
  tropes?: string[];
  pairingType?: string;
  primaryGenre?: string;
  mainSubgenre?: string;
  secondaryGenres?: string[];
  subgenreType?: 'core' | 'crossover' | 'microgenre';
  displayLabel?: string;
  shortLabel?: string;
  refinements?: string[];
  isAnthology?: boolean;
}
interface Series {
  id: string;
  name: string;
  status: string; // 'ongoing', 'completed', 'hiatus'
  books: Book[];
  totalBooks: number;
  isNew?: boolean;
  isDebut?: boolean;
}
interface UnifiedBookCardProps {
  book?: Book;
  series?: Series;
  darkMode?: boolean;
  isCollaboration?: boolean; // Added for collaborations
  isSigned?: boolean; // Show "Signed" badge in hover overlay
}
// Helper function to get color for genre
const getGenreColor = (genre: string): string => {
  if (!genre || typeof genre !== 'string') return 'bg-violet-600';
  const genreLower = genre.toLowerCase();
  if (genreLower.includes('romance')) return 'bg-pink-600';
  if (genreLower.includes('fantasy')) return 'bg-indigo-600';
  if (genreLower.includes('sci-fi') || genreLower.includes('science')) return 'bg-blue-600';
  if (genreLower.includes('mystery')) return 'bg-indigo-600';
  if (genreLower.includes('thriller')) return 'bg-red-600';
  if (genreLower.includes('horror')) return 'bg-gray-800';
  if (genreLower.includes('historical')) return 'bg-amber-600';
  if (genreLower.includes('young adult') || genreLower.includes('ya')) return 'bg-cyan-600';
  if (genreLower.includes('contemporary')) return 'bg-emerald-600';
  return 'bg-violet-600'; // Default color
};
// Helper function to get color for series status
const getStatusColor = (status: string, isNew?: boolean, isDebut?: boolean): string => {
  // Priority: Debut > New > Series Status
  if (isDebut) return 'bg-fuchsia-600';
  if (isNew) return 'bg-indigo-500';
  const statusLower = status?.toLowerCase() || '';
  if (statusLower === 'completed') return 'bg-green-600';
  if (statusLower === 'ongoing') return 'bg-yellow-500';
  if (statusLower.includes('hiatus') || statusLower.includes('discontinued') || statusLower.includes('stopped') || statusLower.includes('removed')) return 'bg-red-600';
  return 'bg-blue-600'; // Default color for unknown status
};
// Helper: series number badge color matches series status (for overlay X/Y badge)
const getSeriesBadgeColor = (status: string, isNew?: boolean, isDebut?: boolean): string => {
  if (isDebut) return 'bg-fuchsia-600/80';
  if (isNew) return 'bg-indigo-500/80';
  const s = status?.toLowerCase() || '';
  if (s === 'completed') return 'bg-green-600/80';
  if (s === 'ongoing') return 'bg-blue-500/80';
  if (s.includes('hiatus')) return 'bg-amber-500/80';
  if (s.includes('discontinued') || s.includes('stopped') || s.includes('removed')) return 'bg-red-600/80';
  return 'bg-indigo-500/80';
};
// Helper function to get color for content tags
const getContentTagColor = (tag: string): string => {
  if (!tag || typeof tag !== 'string') return 'bg-gray-500';
  const tagLower = tag.toLowerCase();
  if (tagLower.includes('slow burn') || tagLower.includes('enemies to lovers') || tagLower.includes('friends to lovers') || tagLower.includes('forbidden')) {
    return 'bg-pink-500'; // Relationship tropes
  }
  if (tagLower.includes('magic') || tagLower.includes('supernatural') || tagLower.includes('paranormal')) {
    return 'bg-indigo-500'; // Fantasy elements
  }
  if (tagLower.includes('suspense') || tagLower.includes('mystery') || tagLower.includes('thriller')) {
    return 'bg-indigo-500'; // Thriller elements
  }
  if (tagLower.includes('dark') || tagLower.includes('violence') || tagLower.includes('mature')) {
    return 'bg-gray-700'; // Dark content
  }
  return 'bg-indigo-500'; // Default color
};
const spiceLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Mild',
    color: 'bg-sky-500/30 text-sky-200'
  },
  2: {
    label: 'Warm',
    color: 'bg-amber-500/30 text-amber-200'
  },
  3: {
    label: 'Hot',
    color: 'bg-orange-500/30 text-orange-200'
  },
  4: {
    label: 'Steamy',
    color: 'bg-rose-500/30 text-rose-200'
  },
  5: {
    label: 'Scorching',
    color: 'bg-red-500/30 text-red-200'
  }
};
const burnLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Slow Burn',
    color: 'bg-cyan-500/30 text-cyan-200'
  },
  2: {
    label: 'Medium Burn',
    color: 'bg-amber-500/30 text-amber-200'
  },
  3: {
    label: 'Fast Burn',
    color: 'bg-orange-500/30 text-orange-200'
  }
};
const darknessLevelMap: Record<number, {
  label: string;
  color: string;
}> = {
  1: {
    label: 'Shadow',
    color: 'bg-slate-400/30 text-slate-200'
  },
  2: {
    label: 'Grey',
    color: 'bg-slate-500/30 text-slate-200'
  },
  3: {
    label: 'Abyss',
    color: 'bg-gray-600/30 text-gray-200'
  },
  4: {
    label: 'Pitch Black',
    color: 'bg-gray-700/40 text-gray-100'
  },
  5: {
    label: 'The Void',
    color: 'bg-gray-800/50 text-white'
  }
};
import { abbreviateGenre } from '../../utils/genreAbbreviations';
const UnifiedBookCard: React.FC<UnifiedBookCardProps> = ({
  book,
  series,
  darkMode = false,
  isCollaboration = false,
  isSigned = false
}) => {
  const [imgError, setImgError] = useState(false);
  const {
    theme,
    content
  } = useBrand();
  const cardConfig = content.bookCard;
  const overlay = theme.coverOverlay;
  const showWarningTags = cardConfig.warningDisplay === 'tags';
  const {
    preferences
  } = useAfterTideUser();
  const isSeries = !!series;
  // Determine which book to display
  // For series, we show the first book's cover
  const displayBook = isSeries ? series.books[0] : book;
  if (!displayBook) return null;
  // Helper to safely coerce a value to string (returns fallback if not a string)
  const safeString = (val: any, fallback: string = ''): string => typeof val === 'string' ? val : fallback;
  // Normalize the book data to handle different schemas
  const normalizedBook = {
    id: displayBook.id,
    title: safeString(displayBook.title, 'Unknown Title'),
    author: safeString(displayBook.author, 'Unknown Author'),
    coverImage: safeString(displayBook.coverImage),
    rating: displayBook.rating || 0,
    reviewCount: displayBook.reviewCount || 0,
    genres: (displayBook.genres || (displayBook.genre ? [displayBook.genre] : [])).filter((g: any) => typeof g === 'string'),
    subgenres: (displayBook.subgenres || (displayBook.subgenre ? [displayBook.subgenre] : [])).filter((s: any) => typeof s === 'string'),
    spiceLevel: displayBook.spiceLevel || 0,
    burnLevel: displayBook.burnLevel || 0,
    darknessLevel: displayBook.darknessLevel || 0,
    endingType: safeString(displayBook.endingType),
    relationshipType: safeString(displayBook.relationshipType),
    // AfterTide books group warnings by category; flatten both shapes
    contentWarnings: flattenWarnings(displayBook.contentWarnings),
    seriesName: isSeries ? safeString(series.name) : safeString(displayBook.seriesName) || safeString(displayBook.series) || '',
    seriesStatus: isSeries ? safeString(series.status) : safeString(displayBook.seriesStatus) || undefined,
    seriesNumber: displayBook.seriesNumber || 0,
    totalSeriesBooks: isSeries ? series.totalBooks : displayBook.totalSeriesBooks || 0,
    formats: Array.isArray(displayBook.formats) ? displayBook.formats.filter((f: any) => typeof f === 'string') : [],
    isNew: isSeries ? series.isNew : displayBook.isNew || false,
    isDebut: isSeries ? series.isDebut : displayBook.isDebut || false,
    coAuthor: safeString(displayBook.coAuthor),
    consentFramework: safeString(displayBook.consentFramework),
    tropes: (displayBook.tropes || []).filter((t: any) => typeof t === 'string'),
    pairingType: safeString(displayBook.pairingType),
    primaryGenre: safeString(displayBook.primaryGenre),
    mainSubgenre: safeString(displayBook.mainSubgenre),
    secondaryGenres: (displayBook.secondaryGenres || []).filter((g: any) => typeof g === 'string'),
    subgenreType: displayBook.subgenreType,
    displayLabel: safeString(displayBook.displayLabel),
    shortLabel: safeString(displayBook.shortLabel),
    refinements: (displayBook.refinements || []).filter((r: any) => typeof r === 'string'),
    isAnthology: !!(displayBook as any).isAnthology
  };
  // Linked editions. Derived from the shared registry rather than a prop, so
  // every surface showing this card agrees on the edition count without each
  // caller having to remember to pass it.
  const linkedStory = isSeries ? undefined : getLinkedStoryForBook(normalizedBook.id);
  // Check if the series name is longer than "Elements Trilogy" (16 characters)
  const isLongSeriesName = normalizedBook.seriesName && normalizedBook.seriesName.length > 16;
  // Determine if the book is part of a romance genre for conditional rendering
  const isRomance = normalizedBook.genres.some(genre => typeof genre === 'string' && genre.toLowerCase().includes('romance'));
  // Count how many tag sections we have to determine spacing
  const hasContentWarnings = normalizedBook.contentWarnings && normalizedBook.contentWarnings.length > 0;
  // Determine if we should show the CW icon on the cover
  const showCWIcon = cardConfig.warningIcon === 'matchesPreferences' ? hasContentWarnings && normalizedBook.contentWarnings.some(w => preferences.excludedWarnings.includes(w)) : hasContentWarnings;
  // Detail links come from the brand's route builders
  const getDetailLink = () => {
    if (isSeries) return content.routes.series(String(series?.id ?? ''));
    if (normalizedBook.isAnthology) {
      return `/anthology/${normalizedBook.id}`;
    }
    return content.routes.book(normalizedBook.id);
  };
  const getAuthorLink = (authorName: string) => {
    if (!authorName || typeof authorName !== 'string') return '#';
    return content.routes.author(authorName);
  };
  const getSeriesLink = (seriesName: string) => {
    if (!seriesName || typeof seriesName !== 'string') return '#';
    return content.routes.series(seriesName);
  };
  const detailLink = getDetailLink();
  const reviewsLink = content.routes.bookReviews(normalizedBook.id);
  // Determine status label text
  const getStatusLabel = () => {
    if (normalizedBook.isDebut) return 'DEBUT';
    if (normalizedBook.isNew) return 'NEW';
    return normalizedBook.seriesStatus?.charAt(0).toUpperCase() + normalizedBook.seriesStatus?.slice(1);
  };
  // Extract co-author from the author string if this is a collaboration
  const getCoAuthor = () => {
    if (normalizedBook.coAuthor) return normalizedBook.coAuthor;
    if (isCollaboration && normalizedBook.author.includes('&')) {
      const authors = normalizedBook.author.split('&');
      return authors[1].trim();
    }
    return '';
  };
  const coAuthor = getCoAuthor();
  const tagRadius = theme.tagRadius;
  return <div className="relative group/card w-full" data-id="element-1075" data-mp="10kghihwsodrx">
      {/* Book Cover */}
      <Link to={detailLink} className={`block relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-[transform,box-shadow,border-color] duration-300 ${overlay.frameHover}`} data-id="element-1076" data-mp="10i3ptd18ip1o">
        {imgError ? <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-100 via-indigo-200 to-indigo-300" data-id="element-1077" data-mp="1f946g5c4xp8w">
            <BookIcon className="h-10 w-10 text-indigo-400 mb-2" data-id="element-1078" data-mp="hrcj6j8mtcqz" />
            <span className="text-indigo-500 text-xs font-medium text-center px-2 line-clamp-2" data-id="element-1079" data-mp="mr8oqmwz9c8n">
              {normalizedBook.title}
            </span>
          </div> : <img src={normalizedBook.coverImage} alt={isSeries ? normalizedBook.seriesName : normalizedBook.title} className="w-full h-full object-cover" loading="lazy" onError={() => setImgError(true)} data-id="element-1080" data-mp="a9o6al60372f" />}
        {/* Genre/Subgenre tag top-left with solid color */}
        {(() => {
        // Use structured displayLabel if available, otherwise fall back to old logic
        if (normalizedBook.displayLabel) {
          const colorGenre = normalizedBook.primaryGenre || normalizedBook.displayLabel;
          return <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(colorGenre)} px-2 py-0.5 ${tagRadius}`} data-id="element-1081" data-mp="3ds7nmsn54sig">
                {abbreviateGenre(normalizedBook.displayLabel)}
              </span>;
        }
        // Legacy fallback
        // AfterTide: show subgenre (more useful for 18+ audience who already know the main genre)
        // LitLaunch: show main genre
        const tagLabel = cardConfig.coverLabel === 'subgenre' ? normalizedBook.subgenres[0] || normalizedBook.genres[0] : normalizedBook.genres[0];
        if (!tagLabel) return null;
        return <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(tagLabel)} px-2 py-0.5 ${tagRadius}`} data-id="element-1082" data-mp="3ap34heayk9sj">
              {abbreviateGenre(tagLabel)}
            </span>;
      })()}

        {/* Content Warning icon indicator - LitLaunch only (AfterTide audience already knows) */}
        {!showWarningTags && showCWIcon && <span className="absolute bottom-2 left-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white shadow-md z-10" title={`Content Warnings: ${normalizedBook.contentWarnings.join(', ')}`} data-id="element-1083" data-mp="zxtknfcekhem">
            <AlertCircleIcon className="h-3.5 w-3.5" data-id="element-1084" data-mp="2ofcv865gtt8p" />
          </span>}
        {/* Linked editions badge — bottom right, clear of the genre tag
            (top-left), status badge (top-right) and CW icon (bottom-left).
            Absent for single-edition books, which look unchanged. */}
        {linkedStory && <span className={`absolute bottom-2 right-2 inline-flex items-center gap-1 text-white text-[10px] font-medium bg-black/70 px-1.5 py-0.5 ${tagRadius} z-10`} title={`${linkedStory.editions.length} linked editions of this story`} data-id="element-1085" data-mp="3856t91yrx4ld">
            <LayersIcon className="h-3 w-3" data-id="element-1086" data-mp="3h40kectc52rj" />
            {linkedStory.editions.length} Editions
          </span>}
        {/* Status badge (series status, new, or debut) - top right */}
        {(normalizedBook.seriesStatus || normalizedBook.isNew || normalizedBook.isDebut) && <span className={`absolute top-2 right-2 text-white text-xs ${getStatusColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} px-2 py-0.5 ${tagRadius}`} data-id="element-1087" data-mp="3hvs0e1f3t3nt">
            {getStatusLabel()}
          </span>}
        {/* Hover Overlay */}
        <div className={`absolute inset-0 ${overlay.scrim} backdrop-blur-sm flex flex-col p-3 transition-opacity duration-200 text-white z-20 opacity-0 group-hover/card:opacity-100`} data-id="element-1088" data-mp="3vv1hd3b4w1su">
          {/* Top section with title/series, author info */}
          <div className="min-w-0" data-id="element-1089" data-mp="9m8i8dlq4g00">
            {isSeries ?
          // Series display
          <>
                <div className="flex items-center justify-between mb-0.5" data-id="element-1090" data-mp="eh5stoynixf0">
                  <Link to={getSeriesLink(normalizedBook.seriesName)} className={`font-bold text-white text-sm line-clamp-1 transition-colors ${overlay.linkHover}`} onClick={e => e.stopPropagation()} data-id="element-1091" data-mp="jd37lqu9nvpo">
                    {normalizedBook.seriesName}
                  </Link>
                  {normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && !isLongSeriesName && <span className={`ml-2 flex-shrink-0 text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded`} data-id="element-1092" data-mp="23y1ejnmvfj8b">
                        {normalizedBook.seriesNumber}/
                        {normalizedBook.totalSeriesBooks}
                      </span>}
                </div>
                {/* Series number on next line for long names */}
                {normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && isLongSeriesName && <span className={`inline-block text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded mb-1`} data-id="element-1093" data-mp="3ajcaspweqzas">
                      Book {normalizedBook.seriesNumber}/
                      {normalizedBook.totalSeriesBooks}
                    </span>}
              </> :
          // Standalone book display
          <>
                <div className="flex items-center justify-between mb-0.5" data-id="element-1094" data-mp="31obszrbyq73y">
                  <h3 className="font-bold text-white text-sm line-clamp-1" data-id="element-1095" data-mp="36m4x8etpgr1l">
                    {normalizedBook.title}
                  </h3>
                  {normalizedBook.seriesName && normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && !isLongSeriesName && <span className={`ml-2 flex-shrink-0 text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded`} data-id="element-1096" data-mp="1h65i8h3eo7e1">
                        {normalizedBook.seriesNumber}/
                        {normalizedBook.totalSeriesBooks}
                      </span>}
                </div>
                {/* Series number on next line for long names */}
                {normalizedBook.seriesNumber > 0 && normalizedBook.totalSeriesBooks > 0 && isLongSeriesName && <span className={`inline-block text-[10px] ${getSeriesBadgeColor(normalizedBook.seriesStatus, normalizedBook.isNew, normalizedBook.isDebut)} text-white px-1.5 py-0.5 rounded mb-1`} data-id="element-1097" data-mp="q0gohwfpgsz2">
                      Book {normalizedBook.seriesNumber}/
                      {normalizedBook.totalSeriesBooks}
                    </span>}
              </>}

            {/* Author */}
            <p className="text-white/80 text-xs line-clamp-1 mb-1.5" data-id="element-1098" data-mp="77cs43b3s20r">
              by{' '}
              <Link to={getAuthorLink(normalizedBook.author)} className={`hover:underline transition-colors ${overlay.linkHover}`} onClick={e => e.stopPropagation()} data-id="element-1099" data-mp="1pgt9ccg7oyzb">
                {normalizedBook.author}
              </Link>
            </p>

            {/* Co-author for collaborations */}
            {isCollaboration && coAuthor && <p className="text-white/80 text-xs line-clamp-1 mb-1.5 -mt-1" data-id="element-1100" data-mp="2895hjftjquhd">
                with{' '}
                <Link to={getAuthorLink(coAuthor)} className={`hover:underline transition-colors ${overlay.linkHover}`} onClick={e => e.stopPropagation()} data-id="element-1101" data-mp="23ac2hiym2vf3">
                  {coAuthor}
                </Link>
              </p>}

            {/* Series name for standalone books */}
            {!isSeries && normalizedBook.seriesName && <div className={`text-[11px] flex items-center mb-1.5 ${overlay.meta}`} data-id="element-1102" data-mp="8ofyn6oa8wmy">
                <BookOpenIcon className="h-3 w-3 mr-1 flex-shrink-0" data-id="element-1103" data-mp="cq162ci1sipr" />
                <Link to={getSeriesLink(normalizedBook.seriesName)} className={`line-clamp-1 transition-colors ${overlay.linkHoverSoft}`} onClick={e => e.stopPropagation()} data-id="element-1104" data-mp="3v3scdj9vzz1j">
                  {normalizedBook.seriesName}
                </Link>
              </div>}

            {/* Edition picker — lets a reader go straight to the version they
                want instead of landing on the default and switching. Each
                links to the same book detail page with its edition selected,
                never to a separate book record. */}
            {linkedStory && <div className="mb-1.5" data-id="element-1105" data-mp="1aeb6in26qyer">
                <span className="block text-[9px] uppercase tracking-wide text-gray-400 mb-1" data-id="element-1106" data-mp="1fccqrbbcl3rt">
                  Choose edition
                </span>
                <div className="flex flex-wrap gap-1" data-id="element-1107" data-mp="1cziwtvd9mxsc">
                  {linkedStory.editions.map(edition => <Link key={edition.id} to={`${detailLink}?edition=${edition.id}`} onClick={e => e.stopPropagation()} className={`px-1.5 py-0.5 ${tagRadius} text-[9px] font-medium bg-white/15 text-white hover:bg-white/30 transition-colors`} data-id="element-1108" data-mp="18ygzxl6tmxtj">
                      {edition.name}
                    </Link>)}
                </div>
              </div>}

            {/* Row 1: Identity Tags (Pairing + Tropes, or Subgenres fallback) */}
            <div className="mb-1.5 flex flex-wrap gap-1" data-id="element-1109" data-mp="1hwj5me1i2en5">
              {/* Signed badge */}
              {isSigned && <span className={`px-1.5 py-0.5 bg-amber-500/80 text-white ${tagRadius} text-[10px] font-medium`} data-id="element-1110" data-mp="2fe9hq9zn5zd7">
                  ✍ Signed
                </span>}

              {/* Identity Tags Logic */}
              {(() => {
              const hasIdentityTags = normalizedBook.pairingType || normalizedBook.tropes && normalizedBook.tropes.length > 0;
              if (hasIdentityTags) {
                const tags = [];
                if (normalizedBook.pairingType) {
                  tags.push({
                    text: normalizedBook.pairingType,
                    type: 'pairing'
                  });
                }
                if (normalizedBook.tropes) {
                  normalizedBook.tropes.slice(0, 2).forEach(trope => {
                    tags.push({
                      text: trope,
                      type: 'trope'
                    });
                  });
                }
                return tags.map((tag, index) => <span key={`id-tag-${index}`} className={`px-1.5 py-0.5 ${tagRadius} text-[9px] ${overlay.tag ?? (tag.type === 'pairing' ? 'bg-pink-500/80 text-white' : `${getContentTagColor(tag.text)}/80 text-white`)}`} data-id="element-1111" data-mp="3oiynicfxle7j" data-mpi={index}>
                      {tag.text}
                    </span>);
              } else if (normalizedBook.subgenres && normalizedBook.subgenres.length > 0) {
                // Fallback to subgenres
                return normalizedBook.subgenres.slice(0, 3).map((subgenre, index) => <span key={`subgenre-${index}`} className={`px-1.5 py-0.5 ${tagRadius} text-[9px] ${overlay.tag ?? `${getGenreColor(subgenre)}/80 text-white`}`} data-id="element-1112" data-mp="g49fdmdha7ls" data-mpi={index}>
                        {abbreviateGenre(subgenre)}
                      </span>);
              }
              return null;
            })()}
            </div>

            {/* Content Warnings */}
            {hasContentWarnings && <div className={`flex items-center mb-1.5 ${showWarningTags ? 'flex-wrap gap-1' : ''}`} data-id="element-1113" data-mp="1z37dabevzddr">
                {showWarningTags ?
            // AfterTide: Show warnings as text tags
            <>
                    {normalizedBook.contentWarnings.slice(0, 3).map((warning, index) => <span key={`cw-${index}`} className={`px-1.5 py-0.5 bg-red-900/30 border border-red-500/30 text-red-300 ${tagRadius} text-[9px]`} data-id="element-1114" data-mp="2ivdoz86x6cxo" data-mpi={index}>
                          {warning}
                        </span>)}
                    {normalizedBook.contentWarnings.length > 3 && <span className={`px-1.5 py-0.5 bg-red-900/30 border border-red-500/30 text-red-300 ${tagRadius} text-[9px]`} data-id="element-1115" data-mp="31l3lbzqbz6rp">
                        +{normalizedBook.contentWarnings.length - 3}
                      </span>}
                  </> :
            // LitLaunch: Show icon
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/30 text-amber-200" title={`Content Warnings: ${normalizedBook.contentWarnings.join(', ')}`} data-id="element-1116" data-mp="2xf85zo1pxfda">
                    <AlertCircleIcon className="h-3 w-3" data-id="element-1117" data-mp="2iqkativsb7nv" />
                  </span>}
              </div>}

            {/* Row 2: Content Levels (Spice, Burn, Darkness, Ending) */}
            <div className="mt-1 flex flex-wrap gap-1" data-id="element-1118" data-mp="29qouz0raaohi">
              {normalizedBook.spiceLevel > 0 && spiceLevelMap[normalizedBook.spiceLevel] && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${spiceLevelMap[normalizedBook.spiceLevel].color}`} data-id="element-1119" data-mp="k9a5cpa91et6">
                    {spiceLevelMap[normalizedBook.spiceLevel].label}
                  </span>}

              {normalizedBook.burnLevel > 0 && burnLevelMap[normalizedBook.burnLevel] && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${burnLevelMap[normalizedBook.burnLevel].color}`} data-id="element-1120" data-mp="1jbvq4qszdnf">
                    {burnLevelMap[normalizedBook.burnLevel].label}
                  </span>}

              {cardConfig.showDarknessLevel && normalizedBook.darknessLevel > 0 && darknessLevelMap[normalizedBook.darknessLevel] && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${darknessLevelMap[normalizedBook.darknessLevel].color}`} data-id="element-1121" data-mp="3iza75u955v1r">
                    {darknessLevelMap[normalizedBook.darknessLevel].label}
                  </span>}

              {normalizedBook.endingType && <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 ${tagRadius} font-medium ${normalizedBook.endingType === 'HEA' || normalizedBook.endingType === 'HFN' ? 'bg-green-600/30 text-green-200' : 'bg-red-500/30 text-red-200'}`} data-id="element-1122" data-mp="2lajizeex5t83">
                  {normalizedBook.endingType === 'HEA' || normalizedBook.endingType === 'HFN' ? `✓ ${normalizedBook.endingType}` : '✕ No HEA'}
                </span>}
            </div>
          </div>

          {/* Bottom section - Rating & Formats */}
          <div className="mt-auto flex items-center justify-between" data-id="element-1123" data-mp="3dyyzy6hm2lm2">
            <Link to={reviewsLink} className={`flex items-center gap-1 transition-colors ${overlay.linkHover}`} onClick={e => e.stopPropagation()} data-id="element-1124" data-mp="3941qiygf6a8n">
              <StarIcon className={`h-4 w-4 ${normalizedBook.isDebut && cardConfig.highlightDebut ? 'text-fuchsia-400' : 'text-amber-400'}`} data-id="element-1125" data-mp="1gxsy1t63gyz" />

              <span className="text-[11px]" data-id="element-1126" data-mp="3nnmbb8zky9a4">
                {normalizedBook.rating > 0 ? normalizedBook.rating : 'New'}
              </span>
              {normalizedBook.reviewCount > 0 && <span className="text-gray-400 text-[11px]" data-id="element-1127" data-mp="3epalvaag9hj9">
                  ({normalizedBook.reviewCount})
                </span>}
            </Link>
            {/* Format icons */}
            {normalizedBook.formats && normalizedBook.formats.length > 0 && <div className="flex items-center gap-1.5" data-id="element-1128" data-mp="2qy50kflqpx1k">
                {normalizedBook.formats.includes('ebook') && <div className="bg-white/15 rounded-full p-1" title="eBook" data-id="element-1129" data-mp="2vwg8gksh5xh5">
                    <TabletIcon className="h-3 w-3 text-white" data-id="element-1130" data-mp="1iwi4fzor9mmf" />
                  </div>}
                {normalizedBook.formats.includes('paperback') && <div className="bg-white/15 rounded-full p-1" title="Paperback" data-id="element-1131" data-mp="1rweofo079xb5">
                    <BookOpenIcon className="h-3 w-3 text-white" data-id="element-1132" data-mp="1wwaqvit91gol" />
                  </div>}
                {normalizedBook.formats.includes('hardcover') && <div className="bg-white/15 rounded-full p-1" title="Hardcover" data-id="element-1133" data-mp="25ve9kloi7u2k">
                    <BookTextIcon className="h-3 w-3 text-white" data-id="element-1134" data-mp="2ano64kk4jsdt" />
                  </div>}
                {normalizedBook.formats.includes('audiobook') && <div className="bg-white/15 rounded-full p-1" title="Audiobook" data-id="element-1135" data-mp="2kmdenmq4s0he">
                    <HeadphonesIcon className="h-3 w-3 text-white" data-id="element-1136" data-mp="2phspe5sudg9l" />
                  </div>}
              </div>}
          </div>
        </div>
      </Link>
    </div>;
};
export default UnifiedBookCard;